import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  BlockStack,
  InlineGrid,
  Text,
  TextField,
  Divider,
  Badge,
  Spinner,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate } from "../../utility/funcs";
import { invoiceDue } from "../../utility/invoicing";
import {
  fetchClientOpenInvoices,
  applyAllocations,
} from "../../utility/payments";

/**
 * Shown after a payment is recorded.
 * Lets the agent allocate the payment to unpaid invoices.
 * Unallocated remainder stays as "pendiente conciliación".
 */
function PaymentAllocationModal({
  open,
  onClose,
  transactionId,
  paymentAmount,
  billingEntityType,
  billingEntityId,
  // When set, the payment was recorded against a specific reservation, so the
  // invoice list is scoped to that reservation only — a payment can never spill
  // onto a sibling reservation's invoice. Unset (company/customer-level
  // payments) keeps the full client-wide list.
  reservationId = null,
  onComplete,
  // Overrides for reusing this modal as a "spend credit" flow.
  // Defaults preserve the original "new payment" copy.
  title = "Conciliar Pago",
  primaryActionLabel = "Conciliar",
  headerLabel = "Pago registrado",
  hideSkip = false,
}) {
  const { session } = UserAuth();
  const [invoices, setInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Synchronous guard against double-clicks on Conciliar — `saving` state
  // doesn't commit until after React's render cycle, so a fast second click
  // can slip past the `disabled` prop and fire applyAllocations twice.
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open || !billingEntityId) return;
    setLoading(true);

    const fetch = async () => {
      const open = await fetchClientOpenInvoices({
        clientType: billingEntityType,
        clientId: billingEntityId,
        reservationId,
      });
      setInvoices(open);

      // Auto-fill greedily (oldest first) — preserves the existing UX.
      let remaining = paymentAmount;
      const auto = {};
      for (const inv of open) {
        const due = inv.due;
        const toApply = Math.min(remaining, due);
        if (toApply > 0) {
          auto[inv.id] = Math.round(toApply * 100) / 100;
          remaining -= toApply;
        }
        if (remaining <= 0.01) break;
      }
      setAllocations(auto);
      setLoading(false);
    };

    fetch();
  }, [open, billingEntityId, billingEntityType, paymentAmount, reservationId]);

  const totalAllocated = Object.values(allocations).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0
  );
  const unallocated = Math.max(0, paymentAmount - totalAllocated);

  const updateAllocation = (invoiceId, value) => {
    const inv = invoices.find((i) => i.id === invoiceId);
    const maxAllowable = inv ? inv.due : 0;
    const val = Math.min(parseFloat(value) || 0, maxAllowable);
    setAllocations({ ...allocations, [invoiceId]: val });
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      // Build the list and delegate to the shared helper so the allocation +
      // invoice + reservation back-update logic stays in one place.
      const list = invoices
        .map((inv) => ({
          invoice: inv,
          amount:
            Math.round((parseFloat(allocations[inv.id]) || 0) * 100) / 100,
        }))
        .filter((a) => a.amount > 0);

      await applyAllocations({
        transactionId,
        transactionAmount: paymentAmount,
        allocations: list,
        createdBy: session?.user?.id,
      });

      if (onComplete) onComplete();
      onClose();
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const skip = async () => {
    // Leave full amount as unallocated (already set on creation)
    if (onComplete) onComplete();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      primaryAction={{
        content: saving ? "Guardando..." : primaryActionLabel,
        onAction: save,
        loading: saving,
        disabled: saving || totalAllocated === 0,
      }}
      secondaryActions={
        hideSkip
          ? undefined
          : [
              {
                content: "Pendiente Conciliación",
                onAction: skip,
                disabled: saving,
              },
            ]
      }
      large
    >
      <Modal.Section>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spinner size="small" />
          </div>
        ) : invoices.length === 0 ? (
          <BlockStack gap="300">
            <Text>
              {reservationId != null
                ? `No hay facturas pendientes de pago para la reservación #${reservationId}.`
                : "No hay facturas pendientes de pago para este cliente."}
            </Text>
            <Text tone="subdued">
              El pago de {formatPrice(paymentAmount)} quedará como{" "}
              <strong>pendiente de conciliación</strong> hasta que se genere una
              factura.
            </Text>
          </BlockStack>
        ) : (
          <BlockStack gap="400">
            <InlineGrid columns="1fr auto">
              <Text variant="headingSm">{headerLabel}</Text>
              <Text variant="headingSm">{formatPrice(paymentAmount)}</Text>
            </InlineGrid>

            <Divider />
            <Text variant="headingSm">
              {reservationId != null
                ? `Facturas de la reservación #${reservationId}`
                : "Facturas pendientes de pago"}
            </Text>

            {invoices.map((inv) => {
              const due = invoiceDue(inv);
              return (
                <InlineGrid key={inv.id} columns="2fr 1fr 1fr 1fr" gap="200">
                  <BlockStack gap="050">
                    <Text variant="bodySm" fontWeight="semibold">
                      {inv.invoice_number || `#${inv.alegra_id}`}
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      {formatDate(inv.date)} · Total: {formatPrice(inv.total)}
                    </Text>
                  </BlockStack>
                  <Text variant="bodySm" tone="subdued">
                    Pagado: {formatPrice(inv.amount_paid || 0)}
                  </Text>
                  <Text variant="bodySm" tone="critical">
                    Debe: {formatPrice(due)}
                  </Text>
                  <TextField
                    value={String(allocations[inv.id] || 0)}
                    onChange={(val) => updateAllocation(inv.id, val)}
                    type="number"
                    prefix="$"
                    autoComplete="off"
                  />
                </InlineGrid>
              );
            })}

            <Divider />
            <InlineGrid columns="1fr auto">
              <Text>Aplicado a facturas</Text>
              <Text tone="success">{formatPrice(totalAllocated)}</Text>
            </InlineGrid>
            <InlineGrid columns="1fr auto">
              <Text fontWeight="bold">
                Pendiente conciliación
              </Text>
              <Text
                fontWeight="bold"
                tone={unallocated > 0.01 ? "attention" : "success"}
              >
                {formatPrice(unallocated)}
              </Text>
            </InlineGrid>
            {unallocated > 0.01 && (
              <Text variant="bodySm" tone="subdued">
                El monto no conciliado quedará como crédito del cliente hasta
                que se genere una nueva factura.
              </Text>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

export default PaymentAllocationModal;
